import { Activity, Clock, Gauge, GitBranch, RefreshCw, Timer, Hourglass, TrendingUp } from "lucide-react";
import { useMemo, type ReactNode } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import type { DeltaMetric, Overview } from "@/lib/analytics/types";
import { DeltaBadge } from "@/components/panels/delta-badge";
import { computeOverviewDelta } from "@/lib/analytics/delta";
import { formatMinutes, formatMs } from "@/lib/utils/format-duration";

interface StatProps {
  label: string;
  value: string;
  hint?: string;
  icon: ReactNode;
  delta?: ReactNode;
}

function Stat({ label, value, hint, icon, delta }: StatProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
        <span className="text-muted-foreground">{icon}</span>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-semibold tabular-nums">{value}</div>
        {delta ? <div className="mt-1">{delta}</div> : null}
        {hint && !delta ? <CardDescription className="mt-1">{hint}</CardDescription> : null}
      </CardContent>
    </Card>
  );
}

interface OverviewCardsProps {
  overview: Overview;
  /** When provided, renders %Δ badges under each metric. */
  compare?: Overview;
}

export function OverviewCards({ overview, compare }: OverviewCardsProps) {
  const successPct = `${(overview.successRate * 100).toFixed(1)}%`;
  const rerunPct = `${(overview.rerunRate * 100).toFixed(1)}%`;

  const delta = useMemo(
    () => (compare != null ? computeOverviewDelta(overview, compare) : null),
    [overview, compare],
  );

  function badge(metric: DeltaMetric) {
    if (!delta) return undefined;
    const cell = delta[metric];
    return <DeltaBadge value={cell.value} direction={cell.direction} />;
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-9">
      <Stat
        label="Total runs"
        value={overview.totalRuns.toLocaleString()}
        hint="Last 90 days"
        icon={<Activity className="size-4" />}
        delta={badge("totalRuns")}
      />
      <Stat
        label="Success rate"
        value={successPct}
        icon={<TrendingUp className="size-4" />}
        delta={badge("successRate")}
      />
      <Stat
        label="p50 duration"
        value={formatMs(overview.p50DurationMs)}
        icon={<Clock className="size-4" />}
        delta={badge("p50DurationMs")}
      />
      <Stat
        label="p95 duration"
        value={formatMs(overview.p95DurationMs)}
        icon={<Timer className="size-4" />}
        delta={badge("p95DurationMs")}
      />
      <Stat
        label="Est. billable minutes"
        value={formatMinutes(overview.estimatedBillableMinutes)}
        hint="Wall-clock minutes"
        icon={<Gauge className="size-4" />}
        delta={badge("estimatedBillableMinutes")}
      />
      <Stat
        label="Active workflows"
        value={overview.activeWorkflows.toString()}
        icon={<GitBranch className="size-4" />}
        delta={badge("activeWorkflows")}
      />
      <Stat
        label="p50 queue time"
        value={formatMs(overview.p50QueueTimeMs)}
        hint="Creation → first step"
        icon={<Hourglass className="size-4" />}
        delta={badge("p50QueueTimeMs")}
      />
      <Stat
        label="p95 queue time"
        value={formatMs(overview.p95QueueTimeMs)}
        icon={<Hourglass className="size-4" />}
        delta={badge("p95QueueTimeMs")}
      />
      <Stat
        label="Re-run rate"
        value={rerunPct}
        hint="Re-runs per first-pass run"
        icon={<RefreshCw className="size-4" />}
        delta={badge("rerunRate")}
      />
    </div>
  );
}
